import { useRef, useEffect } from "react";
import { Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Link2, Highlighter, Type } from "lucide-react";

// Lightweight rich-text editor (contentEditable + execCommand). Emits HTML.
export function sanitizeHtml(html) {
  if (!html) return "";
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/ on\w+="[^"]*"/gi, "")
    .replace(/ on\w+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");
}

const Btn = ({ onClick, title, children, testid }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className="h-8 w-8 flex items-center justify-center rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder = "Write the announcement…" }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    emit();
  };
  const emit = () => onChange?.(sanitizeHtml(ref.current?.innerHTML || ""));

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <Btn onClick={() => exec("bold")} title="Bold" testid="rte-bold"><Bold className="h-4 w-4" /></Btn>
        <Btn onClick={() => exec("italic")} title="Italic" testid="rte-italic"><Italic className="h-4 w-4" /></Btn>
        <Btn onClick={() => exec("underline")} title="Underline" testid="rte-underline"><Underline className="h-4 w-4" /></Btn>
        <span className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
        <Btn onClick={() => exec("insertUnorderedList")} title="Bullet list" testid="rte-ul"><List className="h-4 w-4" /></Btn>
        <Btn onClick={() => exec("insertOrderedList")} title="Numbered list" testid="rte-ol"><ListOrdered className="h-4 w-4" /></Btn>
        <span className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
        <Btn onClick={() => exec("justifyLeft")} title="Align left" testid="rte-left"><AlignLeft className="h-4 w-4" /></Btn>
        <Btn onClick={() => exec("justifyCenter")} title="Align center" testid="rte-center"><AlignCenter className="h-4 w-4" /></Btn>
        <Btn onClick={() => exec("justifyRight")} title="Align right" testid="rte-right"><AlignRight className="h-4 w-4" /></Btn>
        <span className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
        <label className="h-8 px-1.5 flex items-center gap-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer" title="Text colour">
          <Type className="h-4 w-4 text-slate-600 dark:text-slate-300" />
          <input type="color" className="w-5 h-5 border-0 bg-transparent p-0 cursor-pointer" onChange={(e) => exec("foreColor", e.target.value)} data-testid="rte-color" />
        </label>
        <label className="h-8 px-1.5 flex items-center gap-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer" title="Highlight">
          <Highlighter className="h-4 w-4 text-slate-600 dark:text-slate-300" />
          <input type="color" defaultValue="#fff59d" className="w-5 h-5 border-0 bg-transparent p-0 cursor-pointer" onChange={(e) => exec("hiliteColor", e.target.value)} data-testid="rte-highlight" />
        </label>
        <span className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
        <Btn onClick={() => { const url = window.prompt("Link URL:"); if (url) exec("createLink", url); }} title="Insert link" testid="rte-link"><Link2 className="h-4 w-4" /></Btn>
        <select
          className="h-8 ml-1 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-1"
          onChange={(e) => { exec("fontSize", e.target.value); e.target.selectedIndex = 0; }}
          defaultValue=""
          data-testid="rte-fontsize"
        >
          <option value="" disabled>Size</option>
          <option value="2">Small</option>
          <option value="3">Normal</option>
          <option value="5">Large</option>
          <option value="6">X-Large</option>
        </select>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder}
        className="rte-content min-h-[180px] max-h-[360px] overflow-y-auto px-4 py-3 text-sm text-slate-800 dark:text-slate-100 focus:outline-none leading-relaxed"
        data-testid="rte-content"
      />
    </div>
  );
}
